/**
 * KPI Card Component
 *
 * Renders a single headline metric with optional comparison delta.
 * Used as a React component — mounted into the container by the rendering engine
 * via ReactDOM.createRoot().
 */

import React from 'react';
import type { KPICardConfig } from '../../../../types/dashboard';
import { formatValue, formatPercentChange } from '../formatters';
import { DASHBOARD_TOKENS } from '../tokens';

interface KPICardProps {
  config: KPICardConfig;
  data: Record<string, unknown>[];
}

export const KPICard: React.FC<KPICardProps> = ({ config, data }) => {
  const row = data[0] ?? {};
  const primaryValue = Number(row[config.valueField]) || 0;
  const formattedValue = formatValue(primaryValue, config.format, config.currency);

  let comparisonEl: React.ReactNode = null;
  if (config.comparison?.show && config.comparison.previousField) {
    const previousValue = Number(row[config.comparison.previousField]) || 0;
    const changePercent =
      previousValue !== 0
        ? ((primaryValue - previousValue) / Math.abs(previousValue)) * 100
        : 0;
    const isPositive = changePercent > 0;
    const isNegative = changePercent < 0;

    comparisonEl = (
      <div className="flex items-center gap-1.5 mt-2">
        <span
          className="text-sm font-medium"
          style={{
            color: isPositive
              ? DASHBOARD_TOKENS.green
              : isNegative
                ? DASHBOARD_TOKENS.red
                : DASHBOARD_TOKENS.textMuted,
          }}
        >
          {isPositive ? '\u25B2' : isNegative ? '\u25BC' : '\u2014'}{' '}
          {formatPercentChange(changePercent)}
        </span>
        <span
          className="text-xs"
          style={{ color: DASHBOARD_TOKENS.textMuted }}
        >
          {config.comparison.label}
        </span>
      </div>
    );
  }

  const accentColor = config.accentColor || DASHBOARD_TOKENS.blue;

  return (
    <div
      className="relative flex flex-col justify-center h-full px-5 py-4 rounded-lg overflow-hidden"
      style={{
        backgroundColor: DASHBOARD_TOKENS.bgCard,
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      <div
        className="text-3xl font-semibold tracking-tight"
        style={{ color: DASHBOARD_TOKENS.textPrimary }}
      >
        {formattedValue}
      </div>
      {comparisonEl}
    </div>
  );
};

export default KPICard;
